import {
  Clock,
  MapPin,
  Zap,
  Award,
  TrendingUp,
  Users,
  Building,
  Target
} from "lucide-react";
import Container from "@/components/ui/container";

interface Stat {
  number: number;
  suffix?: string;
  label: string;
  icon: React.ElementType;
  description: string;
  color: string;
}

const StatsSection = () => {
  const stats: Stat[] = [
    {
      number: 300000,
      suffix: '+',
      label: 'horas de experiência',
      icon: Clock,
      description: 'Mais de 300 mil horas dedicadas ao desenvolvimento e implementação de soluções Fluig',
      color: 'from-fs-purple-500 to-fs-magenta-500'
    },
    {
      number: 4,
      label: 'capitais brasileiras',
      icon: MapPin,
      description: 'Presença estratégica nas principais capitais do Brasil',
      color: 'from-fs-cyan-500 to-fs-purple-500'
    },
    {
      number: 16,
      label: 'Processos pré-construídos',
      icon: Zap,
      description: 'Portfolio completo de processos prontos para acelerar sua implementação',
      color: 'from-fs-magenta-500 to-fs-cyan-500'
    },
    {
      number: 100,
      suffix: '%',
      label: 'foco em Fluig da TOTVS',
      icon: Award,
      description: 'Especialização total na plataforma Fluig da TOTVS',
      color: 'from-fs-purple-500 to-fs-cyan-500'
    }
  ];

  const formatNumber = (num: number, suffix?: string) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}k${suffix || ''}`;
    }
    return `${num}${suffix || ''}`;
  };

  return (
    <section id="stats" className="py-24 bg-background dark:bg-fs-dark-950 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fs-magenta-500/10 rounded-full blur-3xl" />
      </div>

      <Container className="relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-fs-cyan-500/10 backdrop-blur-sm border border-fs-cyan-500/30 rounded-full px-4 py-2 mb-6">
            <TrendingUp className="w-4 h-4 text-fs-cyan-400" />
            <span className="text-muted-foreground dark:text-white/70 text-sm font-medium">
              Nossos Números
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold font-poppins text-foreground dark:text-white mb-6">
            Provas reais do
            <span className="block fs-gradient-text mt-2">
              impacto que geramos no Fluig
            </span>
          </h2>

          <p className="text-xl text-muted-foreground dark:text-white/70 leading-relaxed">
            Números que refletem nossa dedicação, experiência e compromisso com a excelência 
            em soluções Fluig para empresas de todos os portes.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            
            return (
              <div 
                key={stat.label}
                className="group relative bg-card border border-border rounded-3xl p-8 hover:-translate-y-2 transition-all duration-500 hover:shadow-2xl hover:shadow-fs-purple-500/20"
              >
                {/* Gradient Background Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-500`} />
                
                {/* Icon */}
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.color} p-4 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="w-full h-full text-white" />
                </div>

                {/* Number */}
                <div className="mb-4">
                  <div className="flex items-baseline">
                    <span className="text-4xl md:text-5xl font-bold font-poppins fs-gradient-text">
                      {formatNumber(stat.number)}
                    </span>
                    {stat.suffix && (
                      <span className="text-2xl md:text-3xl font-bold text-fs-cyan-400 ml-1">
                        {stat.suffix}
                      </span>
                    )}
                  </div>
                  
                  <div className="h-1 w-full bg-gray-200 dark:bg-fs-purple-500/30 rounded-full mt-3 overflow-hidden">
                    <div 
                      className={`h-full bg-gradient-to-r ${stat.color} w-full`}
                    />
                  </div>
                </div>

                {/* Label */}
                <h3 className="text-lg font-semibold text-foreground dark:text-white mb-3 group-hover:text-fs-cyan-300 transition-colors">
                  {stat.label}
                </h3>

                {/* Description */}
                <p className="text-sm text-muted-foreground dark:text-white/70 leading-relaxed group-hover:text-muted-foreground/90 dark:group-hover:text-white/90 transition-colors">
                  {stat.description}
                </p>

                {/* Hover indicator */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-2 h-2 bg-fs-cyan-400 rounded-full animate-pulse" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Section */}
        <div className="mt-20 text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Trust Indicator */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 p-3 mb-4">
                <Users className="w-full h-full text-white" />
              </div>
              <h4 className="text-lg font-semibold text-foreground dark:text-white mb-2">Equipe Especializada</h4>
              <p className="text-muted-foreground dark:text-white/70 text-sm">Profissionais certificados e experientes</p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-fs-cyan-500 to-fs-magenta-500 p-3 mb-4">
                <Building className="w-full h-full text-white" />
              </div>
              <h4 className="text-lg font-semibold text-foreground dark:text-white mb-2">Empresas Atendidas</h4>
              <p className="text-muted-foreground dark:text-white/70 text-sm">Desde startups até grandes corporações</p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-fs-magenta-500 to-fs-purple-500 p-3 mb-4">
                <Target className="w-full h-full text-white" />
              </div>
              <h4 className="text-lg font-semibold text-foreground dark:text-white mb-2">Resultados Comprovados</h4>
              <p className="text-muted-foreground dark:text-white/70 text-sm">Projetos entregues com sucesso e satisfação</p>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default StatsSection;